import type { LocalizedCopy } from "../productCopyTypes";

function paragraphs(text: string): string {
  return text
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>\n${p}\n</p>`)
    .join("\n\n");
}

function featureList(items: string[]): string {
  return `<ul>\n${items.map((item) => `  <li>${item}</li>`).join("\n")}\n</ul>`;
}

const COPY = {
  en: {
    title: "Leopard 1 Medium Tank",
    description:
      "Leopard 1 is a large-scale 12GO military brick set designed for collectors and builders who appreciate historically inspired armored vehicles and detailed construction. The set contains 1,862+ pieces and is built in 1:28 scale, resulting in a substantial display piece with a detailed exterior and an extensive internal structure.\n\nThe design focuses on the characteristic appearance of the Leopard 1, including its low-profile hull, rounded turret, long main gun, road wheels, tracks and numerous external details. The construction is highly modular, allowing major sections of the vehicle to be separated during the building process and exposing the internal structure.\n\nThe set features a functional aiming system, with the main gun capable of elevation and a rotating turret. Several hatches and access panels can be opened, revealing details inside the turret and hull. The interior includes an ammunition compartment, crew-related elements and a detailed engine section, giving the set considerably more depth than a purely external display build.\n\nThe suspension and running gear are also carefully represented. Multiple road wheels, tracks and mechanical details contribute to the authentic proportions and give the finished vehicle a convincing armored appearance.\n\nWith its large scale, extensive piece count and combination of external and internal details, Leopard 1 is designed primarily as a collector-oriented display set, while still offering a rewarding and technically interesting building experience.",
    historicalTitle: "Historical Background",
    historical:
      "The Leopard 1 was a West German main battle tank developed during the post-war period as the Bundeswehr's standard armored fighting vehicle. Development began in the 1950s, and the first production vehicles entered service in 1965.\n\nUnlike heavily armored tanks that prioritized maximum protection, the Leopard 1 emphasized mobility, firepower and battlefield flexibility. Its relatively light construction and powerful engine gave it excellent mobility compared with many contemporary main battle tanks.\n\nThe vehicle was initially armed with the highly effective 105 mm Royal Ordnance L7A3 gun, a weapon that became widely used by NATO armored forces. The Leopard 1 also featured a sophisticated fire-control and sighting arrangement for its time, allowing the crew to engage targets effectively at combat ranges.\n\nThe Leopard 1 underwent a number of modernization programs during its service life. Improved fire-control equipment, night-vision systems, protection and other upgrades were introduced across later variants. The basic design was also adapted into numerous specialized vehicles, including recovery vehicles, engineering vehicles and anti-aircraft platforms.\n\nThe tank served for decades in Germany and was exported to a number of countries. Its combination of mobility, relatively simple design and upgrade potential helped make it one of the most widely recognized European armored vehicles of the Cold War era.",
    highlightsTitle: "Product Features",
    features: [
      "Brand: 12GO",
      "Set Number: 97055",
      "Name: Leopard 1 Medium Tank",
      "Pieces: 1,862+",
      "Scale: 1:28",
      "Large-scale military brick set",
      "Rotating turret",
      "Elevating main gun",
      "Detailed aiming system",
      "Multiple opening hatches",
      "Detailed ammunition compartment",
      "Detailed engine section",
      "Modular layered construction",
      "Detailed suspension and running gear",
      "Realistic tracks and road wheels",
      "Suitable for display and collection",
    ],
    specsTitle: "Specifications",
    specs: [
      "<li><strong>Brand:</strong> 12GO</li>",
      "<li><strong>Set Number:</strong> 97055</li>",
      "<li><strong>Name:</strong> Leopard 1</li>",
      "<li><strong>Pieces:</strong> 1,862+</li>",
      "<li><strong>Scale:</strong> 1:28</li>",
    ],
  },
  pl: {
    title: "Leopard 1 Medium Tank",
    description:
      "Leopard 1 to duży, szczegółowy set militarny 12GO przeznaczony dla konstruktorów i kolekcjonerów zainteresowanych pojazdami pancernymi oraz realistycznymi konstrukcjami. Zestaw składa się z ponad 1862 elementów i został wykonany w skali 1:28, dzięki czemu po zbudowaniu prezentuje się jako duży i efektowny element kolekcji.\n\nKonstrukcja wiernie oddaje charakterystyczną sylwetkę Leoparda 1, obejmującą nisko umieszczony kadłub, zaokrągloną wieżę, długą lufę, układ jezdny oraz liczne detale zewnętrzne. Konstrukcja ma budowę modułową, dzięki czemu poszczególne sekcje można rozdzielić, odsłaniając elementy znajdujące się wewnątrz.\n\nSet wyposażono w funkcjonalny system celowania. Wieża może się obracać, a główna armata może być podnoszona i opuszczana. Liczne włazy oraz panele dostępowe można otwierać, pokazując szczegóły wnętrza wieży i kadłuba.\n\nWewnątrz znajdują się między innymi elementy imitujące magazyn amunicji, przestrzeń załogi oraz szczegółowo odwzorowany przedział silnikowy. Dzięki temu konstrukcja jest interesująca nie tylko z zewnątrz, ale również po odsłonięciu jej wewnętrznych sekcji.\n\nRozbudowany układ zawieszenia obejmuje liczne koła jezdne, gąsienice i elementy mechaniczne, które pomagają zachować charakterystyczne proporcje prawdziwego pojazdu.",
    historicalTitle: "Tło historyczne",
    historical:
      "Leopard 1 był zachodnioniemieckim czołgiem podstawowym opracowanym w okresie powojennym jako standardowy pojazd Bundeswehry. Prace nad konstrukcją rozpoczęto w latach 50., a pierwsze seryjne egzemplarze trafiły do służby w 1965 roku.\n\nW przeciwieństwie do konstrukcji kładących szczególny nacisk na maksymalną ochronę pancerną, Leopard 1 został zaprojektowany z dużym naciskiem na mobilność, siłę ognia i elastyczność wykorzystania. Stosunkowo niewielka masa oraz mocny silnik zapewniały bardzo dobre właściwości terenowe.\n\nPodstawowe uzbrojenie stanowiła skuteczna armata 105 mm Royal Ordnance L7A3, szeroko wykorzystywana również w innych konstrukcjach państw NATO. Czołg otrzymał również zaawansowane jak na swoje czasy przyrządy obserwacyjne i systemy kierowania ogniem.\n\nW trakcie wieloletniej służby Leopard 1 był wielokrotnie modernizowany. Kolejne warianty otrzymywały ulepszone systemy celownicze, wyposażenie noktowizyjne, dodatkowe rozwiązania zwiększające ochronę oraz modernizacje układu elektronicznego.\n\nPodwozie Leoparda 1 wykorzystano również do budowy licznych pojazdów specjalistycznych, między innymi wozów zabezpieczenia technicznego, pojazdów saperskich i innych konstrukcji przeznaczonych do współpracy z wojskami pancernymi.",
    highlightsTitle: "Cechy produktu",
    features: [
      "Marka: 12GO",
      "Numer zestawu: 97055",
      "Nazwa: Leopard 1 Medium Tank",
      "Liczba elementów: 1862+",
      "Skala: 1:28",
      "Duży militarny set kolekcjonerski",
      "Obracana wieża",
      "Regulowana w pionie armata główna",
      "Funkcjonalny system celowania",
      "Otwierane włazy",
      "Szczegółowy magazyn amunicji",
      "Szczegółowy przedział silnikowy",
      "Modułowa konstrukcja",
      "Rozbudowane zawieszenie i układ jezdny",
      "Szczegółowe gąsienice i koła",
      "Przeznaczony do ekspozycji i kolekcjonowania",
    ],
    specsTitle: "Specyfikacja",
    specs: [
      "<li><strong>Marka:</strong> 12GO</li>",
      "<li><strong>Numer zestawu:</strong> 97055</li>",
      "<li><strong>Nazwa:</strong> Leopard 1</li>",
      "<li><strong>Liczba elementów:</strong> 1862+</li>",
      "<li><strong>Skala:</strong> 1:28</li>",
    ],
  },
  de: {
    title: "Leopard 1 Medium Tank",
    description:
      "Leopard 1 ist ein großformatiges militärisches Klemmbaustein-Set von 12GO für Sammler und Modellbau-Enthusiasten. Mit 1.862+ Teilen und einem Maßstab von 1:28 entsteht ein besonders großes Ausstellungsmodell mit umfangreichen Außen- und Innendetails.\n\nDas Set orientiert sich an der charakteristischen Form des Leopard 1 mit seinem flachen Wannenaufbau, dem abgerundeten Turm, der langen Hauptkanone und dem detaillierten Laufwerk. Die Konstruktion ist modular aufgebaut und ermöglicht das Abnehmen wichtiger Baugruppen.\n\nZu den Funktionen gehört ein bewegliches Ziel- und Richtsystem. Der Turm lässt sich drehen, während die Hauptkanone in der Höhe verstellbar ist. Mehrere Luken und Abdeckungen können geöffnet werden und geben den Blick auf die Innenbereiche frei.\n\nIm Inneren befinden sich unter anderem ein detailliertes Munitionsfach, Elemente des Kampfraums und ein ausgearbeiteter Motorbereich. Dadurch bietet das Set auch bei geöffneten Baugruppen zahlreiche Details.",
    historicalTitle: "Historischer Hintergrund",
    historical:
      "Der Leopard 1 war ein westdeutscher Kampfpanzer, der in der Nachkriegszeit für die Bundeswehr entwickelt wurde. Die Entwicklung begann in den 1950er-Jahren, und die ersten Serienfahrzeuge wurden 1965 in Dienst gestellt.\n\nBei seiner Entwicklung wurde besonderer Wert auf Beweglichkeit, Feuerkraft und taktische Flexibilität gelegt. Im Vergleich zu stärker gepanzerten Zeitgenossen verfügte der Leopard 1 über eine relativ geringe Masse und eine leistungsfähige Motorisierung, wodurch er eine hohe Mobilität erreichte.\n\nDie Hauptbewaffnung bestand aus der 105-mm-Kanone Royal Ordnance L7A3, die auch von zahlreichen anderen NATO-Streitkräften eingesetzt wurde.\n\nWährend seiner langen Dienstzeit wurde der Leopard 1 mehrfach modernisiert. Verbesserungen betrafen unter anderem Feuerleitsysteme, Nachtkampfausrüstung, Schutzmaßnahmen und elektronische Systeme.\n\nDas Fahrgestell des Leopard 1 diente außerdem als Grundlage für zahlreiche Spezialfahrzeuge, darunter Bergefahrzeuge und Pionierfahrzeuge.",
    highlightsTitle: "Produkteigenschaften",
    features: [
      "Marke: 12GO",
      "Setnummer: 97055",
      "Name: Leopard 1 Medium Tank",
      "Teile: 1.862+",
      "Maßstab: 1:28",
      "Großformatiges Militär-Set",
      "Drehbarer Turm",
      "Höhenverstellbare Hauptkanone",
      "Bewegliches Zielsystem",
      "Mehrere zu öffnende Luken",
      "Detailliertes Munitionsfach",
      "Detaillierter Motorbereich",
      "Modulare Konstruktion",
      "Detailliertes Laufwerk",
      "Ausgearbeitete Ketten und Laufrollen",
      "Für Ausstellung und Sammlung geeignet",
    ],
    specsTitle: "Technische Daten",
    specs: [
      "<li><strong>Marke:</strong> 12GO</li>",
      "<li><strong>Setnummer:</strong> 97055</li>",
      "<li><strong>Name:</strong> Leopard 1</li>",
      "<li><strong>Teile:</strong> 1.862+</li>",
      "<li><strong>Maßstab:</strong> 1:28</li>",
    ],
  },
  fr: {
    title: "Leopard 1 Medium Tank",
    description:
      "Leopard 1 est un grand set de construction militaire 12GO conçu pour les collectionneurs et les passionnés de véhicules blindés. Avec plus de 1 862 pièces et une échelle 1:28, il offre un modèle imposant avec de nombreux détails extérieurs et intérieurs.\n\nLa construction reproduit la silhouette caractéristique du Leopard 1, notamment sa caisse basse, sa tourelle arrondie, son canon long et son train de roulement détaillé.\n\nLe set dispose d'un système de visée fonctionnel, d'une tourelle rotative et d'un canon principal réglable en élévation. Plusieurs trappes peuvent être ouvertes afin de révéler les détails internes.\n\nL'intérieur comprend notamment un compartiment de munitions, des éléments de l'espace de l'équipage et un compartiment moteur détaillé.",
    historicalTitle: "Contexte historique",
    historical:
      "Le Leopard 1 est un char de combat ouest-allemand développé après la Seconde Guerre mondiale pour équiper la Bundeswehr. Son développement débuta dans les années 1950 et les premiers exemplaires de série entrèrent en service en 1965.\n\nLa conception privilégiait la mobilité, la puissance de feu et la flexibilité plutôt qu'un blindage extrêmement lourd. Son moteur puissant et son poids relativement contenu lui permettaient de bénéficier d'une excellente mobilité.\n\nSon armement principal était constitué du canon de 105 mm Royal Ordnance L7A3, utilisé par de nombreux pays membres de l'OTAN.\n\nLe Leopard 1 a connu plusieurs programmes de modernisation au cours de sa carrière, avec des améliorations concernant notamment les systèmes de conduite de tir, les équipements de vision nocturne et différents éléments de protection.\n\nSon châssis a également été utilisé pour développer plusieurs véhicules spécialisés.",
    highlightsTitle: "Caractéristiques",
    features: [
      "Marque : 12GO",
      "Référence : 97055",
      "Nom : Leopard 1 Medium Tank",
      "Nombre de pièces : 1 862+",
      "Échelle : 1:28",
      "Grand set militaire de collection",
      "Tourelle rotative",
      "Canon principal réglable",
      "Système de visée fonctionnel",
      "Plusieurs trappes ouvrantes",
      "Compartiment de munitions détaillé",
      "Compartiment moteur détaillé",
      "Construction modulaire",
      "Train de roulement détaillé",
      "Chenilles et roues fidèlement représentées",
      "Convient à l'exposition et à la collection",
    ],
    specsTitle: "Spécifications",
    specs: [
      "<li><strong>Marque :</strong> 12GO</li>",
      "<li><strong>Référence :</strong> 97055</li>",
      "<li><strong>Nom :</strong> Leopard 1</li>",
      "<li><strong>Nombre de pièces :</strong> 1 862+</li>",
      "<li><strong>Échelle :</strong> 1:28</li>",
    ],
  },
  es: {
    title: "Leopard 1 Medium Tank",
    description:
      "Leopard 1 es un gran set de construcción militar de 12GO pensado para coleccionistas y aficionados a los vehículos blindados. Con más de 1.862 piezas y una escala 1:28, ofrece una construcción de gran tamaño con numerosos detalles interiores y exteriores.\n\nEl diseño reproduce los principales rasgos del Leopard 1, incluyendo el casco de perfil bajo, la torreta redondeada, el largo cañón y el tren de rodaje.\n\nEl set incorpora un sistema de apuntado funcional, una torreta giratoria y un cañón principal regulable en elevación. Varias escotillas pueden abrirse para mostrar el interior.",
    historicalTitle: "Contexto histórico",
    historical:
      "El Leopard 1 fue un carro de combate de Alemania Occidental desarrollado durante el periodo posterior a la Segunda Guerra Mundial para la Bundeswehr. Su desarrollo comenzó en la década de 1950 y los primeros vehículos de producción entraron en servicio en 1965.\n\nSu diseño puso especial énfasis en la movilidad, la potencia de fuego y la flexibilidad táctica. Su peso relativamente reducido y su potente motor le proporcionaban una buena capacidad de movimiento en diferentes terrenos.\n\nEl armamento principal estaba formado por el cañón de 105 mm Royal Ordnance L7A3, utilizado también por numerosas fuerzas armadas de la OTAN.\n\nDurante su larga vida operativa, el Leopard 1 recibió diferentes programas de modernización que mejoraron sus sistemas de dirección de tiro, visión nocturna y otros elementos de protección y equipamiento.\n\nSu chasis también fue utilizado como base para diferentes vehículos especializados.",
    highlightsTitle: "Características",
    features: [
      "Marca: 12GO",
      "Número de set: 97055",
      "Nombre: Leopard 1 Medium Tank",
      "Piezas: 1.862+",
      "Escala: 1:28",
      "Gran set militar para coleccionistas",
      "Torreta giratoria",
      "Cañón principal elevable",
      "Sistema de apuntado funcional",
      "Varias escotillas abatibles",
      "Compartimento de munición detallado",
      "Compartimento del motor detallado",
      "Construcción modular",
      "Tren de rodaje detallado",
      "Cadenas y ruedas cuidadosamente reproducidas",
      "Ideal para exposición y colección",
    ],
    specsTitle: "Especificaciones",
    specs: [
      "<li><strong>Marca:</strong> 12GO</li>",
      "<li><strong>Número de set:</strong> 97055</li>",
      "<li><strong>Nombre:</strong> Leopard 1</li>",
      "<li><strong>Piezas:</strong> 1.862+</li>",
      "<li><strong>Escala:</strong> 1:28</li>",
    ],
  },
  nl: {
    title: "Leopard 1 Medium Tank",
    description:
      "Leopard 1 is een groot militair bouwstenenmodel van 12GO, ontworpen voor verzamelaars en liefhebbers van gepantserde voertuigen. Met 1.862+ onderdelen en een schaal van 1:28 biedt het model een indrukwekkende bouwervaring en een uitgebreid displaymodel.\n\nDe set is gebaseerd op de kenmerkende vorm van de Leopard 1, met de lage romp, afgeronde toren, lange hoofdkanon en gedetailleerde rupsbanden en wielophanging.\n\nDe constructie beschikt over een functioneel richtsysteem, een draaibare toren en een in hoogte verstelbaar hoofdkanon. Verschillende luiken kunnen worden geopend om details van het interieur zichtbaar te maken.",
    historicalTitle: "Historische achtergrond",
    historical:
      "De Leopard 1 was een West-Duitse gevechtstank die na de Tweede Wereldoorlog werd ontwikkeld voor de Bundeswehr. De ontwikkeling begon in de jaren 1950 en de eerste productietanks kwamen in 1965 in dienst.\n\nBij het ontwerp werd sterk ingezet op mobiliteit, vuurkracht en tactische flexibiliteit. Dankzij zijn relatief lage gewicht en krachtige motor beschikte de Leopard 1 over uitstekende mobiliteit.\n\nDe belangrijkste bewapening bestond uit het 105 mm Royal Ordnance L7A3-kanon, dat ook door verschillende NAVO-landen werd gebruikt.\n\nTijdens zijn lange operationele loopbaan werd de Leopard 1 meerdere keren gemoderniseerd. Onder andere vuurleidingssystemen, nachtzichtapparatuur en beschermingsvoorzieningen werden verbeterd.\n\nHet chassis werd bovendien gebruikt als basis voor verschillende gespecialiseerde voertuigen.",
    highlightsTitle: "Productkenmerken",
    features: [
      "Merk: 12GO",
      "Setnummer: 97055",
      "Naam: Leopard 1 Medium Tank",
      "Onderdelen: 1.862+",
      "Schaal: 1:28",
      "Groot militair collectorset",
      "Draaibare toren",
      "In hoogte verstelbaar hoofdkanon",
      "Functioneel richtsysteem",
      "Meerdere te openen luiken",
      "Gedetailleerd munitiecompartiment",
      "Gedetailleerd motorcompartiment",
      "Modulaire constructie",
      "Gedetailleerd onderstel",
      "Uitgewerkte rupsbanden en wielen",
      "Geschikt voor display en collectie",
    ],
    specsTitle: "Specificaties",
    specs: [
      "<li><strong>Merk:</strong> 12GO</li>",
      "<li><strong>Setnummer:</strong> 97055</li>",
      "<li><strong>Naam:</strong> Leopard 1</li>",
      "<li><strong>Onderdelen:</strong> 1.862+</li>",
      "<li><strong>Schaal:</strong> 1:28</li>",
    ],
  },
  it: {
    title: "Leopard 1 Medium Tank",
    description:
      "Leopard 1 è un grande set militare 12GO pensato per collezionisti e appassionati di veicoli corazzati. Con oltre 1.862 pezzi e una scala 1:28, offre una costruzione di grandi dimensioni ricca di dettagli interni ed esterni.\n\nIl design riproduce la caratteristica configurazione del Leopard 1, con scafo ribassato, torretta arrotondata, cannone principale lungo e dettagliato sistema di rotolamento.\n\nIl set comprende un sistema di puntamento funzionale, una torretta girevole e un cannone principale regolabile in elevazione. Diverse botole possono essere aperte per mostrare gli elementi interni.",
    historicalTitle: "Contesto storico",
    historical:
      "Il Leopard 1 è stato un carro armato da combattimento della Germania Ovest sviluppato nel dopoguerra per la Bundeswehr. Lo sviluppo iniziò negli anni '50 e i primi esemplari di serie entrarono in servizio nel 1965.\n\nIl progetto poneva grande attenzione a mobilità, potenza di fuoco e flessibilità. Il peso relativamente contenuto e il potente motore garantivano al carro eccellenti capacità di movimento.\n\nL'armamento principale era costituito dal cannone da 105 mm Royal Ordnance L7A3, utilizzato anche da numerose forze armate della NATO.\n\nDurante la sua lunga carriera il Leopard 1 fu sottoposto a diversi programmi di modernizzazione, con aggiornamenti ai sistemi di tiro, alle apparecchiature per la visione notturna e ad altri componenti.\n\nIl suo telaio venne inoltre utilizzato per numerosi veicoli specializzati.",
    highlightsTitle: "Caratteristiche",
    features: [
      "Marca: 12GO",
      "Numero set: 97055",
      "Nome: Leopard 1 Medium Tank",
      "Pezzi: 1.862+",
      "Scala: 1:28",
      "Grande set militare da collezione",
      "Torretta girevole",
      "Cannone principale regolabile",
      "Sistema di puntamento funzionale",
      "Diverse botole apribili",
      "Vano munizioni dettagliato",
      "Vano motore dettagliato",
      "Costruzione modulare",
      "Sistema di sospensione dettagliato",
      "Cingoli e ruote dettagliati",
      "Ideale per esposizione e collezione",
    ],
    specsTitle: "Specifiche",
    specs: [
      "<li><strong>Marca:</strong> 12GO</li>",
      "<li><strong>Numero set:</strong> 97055</li>",
      "<li><strong>Nome:</strong> Leopard 1</li>",
      "<li><strong>Pezzi:</strong> 1.862+</li>",
      "<li><strong>Scala:</strong> 1:28</li>",
    ],
  },
  ru: {
    title: "Leopard 1 Medium Tank",
    description:
      "Leopard 1 — крупный военный набор 12GO из конструктора, созданный для коллекционеров и любителей бронетехники. Набор включает 1862+ деталей и выполнен в масштабе 1:28, благодаря чему собранная машина имеет внушительный размер.\n\nКонструкция передаёт характерные особенности Leopard 1: низкий корпус, округлую башню, длинное основное орудие и детализированную ходовую часть.\n\nМодель оснащена функциональной системой наведения. Башня вращается, а основное орудие регулируется по вертикали. Несколько люков можно открывать, демонстрируя внутренние элементы.\n\nВнутри предусмотрены детализированный отсек боекомплекта, элементы боевого отделения и моторный отсек.",
    historicalTitle: "Историческая справка",
    historical:
      "Leopard 1 — западногерманский основной боевой танк, разработанный в послевоенный период для вооружённых сил ФРГ. Разработка началась в 1950-х годах, а первые серийные машины поступили на вооружение в 1965 году.\n\nПри проектировании основное внимание уделялось мобильности, огневой мощи и тактической гибкости. Относительно небольшая масса и мощный двигатель обеспечивали танку высокую подвижность.\n\nОсновным вооружением стала 105-мм пушка Royal Ordnance L7A3, которая использовалась также на многих других танках стран НАТО.\n\nНа протяжении длительной службы Leopard 1 неоднократно модернизировался. Обновлялись системы управления огнём, средства ночного видения и другие элементы оборудования и защиты.\n\nШасси Leopard 1 также стало основой для создания различных специализированных машин.",
    highlightsTitle: "Характеристики",
    features: [
      "Бренд: 12GO",
      "Номер набора: 97055",
      "Название: Leopard 1 Medium Tank",
      "Количество деталей: 1862+",
      "Масштаб: 1:28",
      "Крупный военный коллекционный набор",
      "Поворотная башня",
      "Регулируемое по высоте орудие",
      "Функциональная система наведения",
      "Открывающиеся люки",
      "Детализированный отсек боекомплекта",
      "Детализированный двигатель",
      "Модульная конструкция",
      "Детализированная ходовая часть",
      "Проработанные гусеницы и опорные катки",
      "Подходит для экспозиции и коллекции",
    ],
    specsTitle: "Спецификация",
    specs: [
      "<li><strong>Бренд:</strong> 12GO</li>",
      "<li><strong>Номер набора:</strong> 97055</li>",
      "<li><strong>Название:</strong> Leopard 1</li>",
      "<li><strong>Количество деталей:</strong> 1862+</li>",
      "<li><strong>Масштаб:</strong> 1:28</li>",
    ],
  },
  zh: {
    title: "Leopard 1 Medium Tank",
    description:
      "Leopard 1 是一款面向军事模型收藏者和积木爱好者的大型 12GO 装甲车辆积木套装。整套包含 1862+ 个零件，采用 1:28 比例，完成后具有较大的展示尺寸和丰富的细节表现。\n\n模型还原了 Leopard 1 的典型外形，包括低矮的车体、圆润的炮塔、细长的主炮以及完整的履带和行走机构。\n\n套装配备了可操作的瞄准系统，炮塔可以旋转，主炮可以进行俯仰调节。同时设计了多个可开启的舱门，可以观察内部结构。\n\n内部包含弹药舱、乘员区域以及发动机细节，使这款套装不仅适合从外部欣赏，也具有丰富的内部展示效果。",
    historicalTitle: "历史背景",
    historical:
      "Leopard 1 是西德在二战后为联邦国防军研发的主战坦克。研发工作始于20世纪50年代，首批量产车辆于 1965年 正式服役。\n\nLeopard 1 的设计重点并不是单纯追求厚重装甲，而是强调机动性、火力和战场灵活性。相对较轻的车体重量以及强劲的发动机，使其拥有出色的越野机动能力。\n\n该坦克的主要武器为 105毫米 Royal Ordnance L7A3 火炮，这一火炮也被多个北约国家的装甲车辆采用。\n\n在长期服役过程中，Leopard 1 曾进行多次现代化升级，包括火控系统、夜视设备以及防护和电子设备等方面的改进。\n\nLeopard 1 的底盘还被广泛用于开发维修、工程和其他专业用途的装甲车辆。",
    highlightsTitle: "产品特点",
    features: [
      "品牌： 12GO",
      "产品编号： 97055",
      "名称： Leopard 1 Medium Tank",
      "积木数量： 1862+ 块",
      "比例： 1:28",
      "大型军事收藏级积木套装",
      "可旋转炮塔",
      "主炮可上下调节",
      "可操作瞄准系统",
      "多处可开启舱门",
      "详细弹药舱",
      "详细发动机区域",
      "模块化结构",
      "精细行走机构",
      "细致履带和负重轮",
      "适合展示和收藏",
    ],
    specsTitle: "产品规格",
    specs: [
      "<li><strong>品牌：</strong> 12GO</li>",
      "<li><strong>产品编号：</strong> 97055</li>",
      "<li><strong>名称：</strong> Leopard 1</li>",
      "<li><strong>积木数量：</strong> 1862+ 块</li>",
      "<li><strong>比例：</strong> 1:28</li>",
    ],
  },
} as const;

type LocaleKey = keyof typeof COPY;

function buildLocaleHtml(locale: LocaleKey): string {
  const c = COPY[locale] ?? COPY.en;
  return `<h1>${c.title}</h1>

${paragraphs(c.description)}

<h2>${c.specsTitle}</h2>

<ul>
${c.specs.join("\n")}
</ul>

<h2>${c.highlightsTitle}</h2>

${featureList([...c.features])}

<h2>${c.historicalTitle}</h2>

${paragraphs(c.historical)}`;
}

export const RICH_PRODUCT_COPY_97055: LocalizedCopy = {
  en: buildLocaleHtml("en"),
  pl: buildLocaleHtml("pl"),
  de: buildLocaleHtml("de"),
  es: buildLocaleHtml("es"),
  fr: buildLocaleHtml("fr"),
  nl: buildLocaleHtml("nl"),
  zh: buildLocaleHtml("zh"),
  it: buildLocaleHtml("it"),
  ru: buildLocaleHtml("ru"),
};
